import { buildCollection } from "@firecms/core";
import { type CaseArticle } from "@one-ai/shared/cms/schema";
import type { CollectionStrings } from "../i18n/collection-strings";

/**
 * Typed with `CaseArticle` from the shared schema, same as the announcements
 * collection.
 *
 * `img` is a plain URL field, not a `storage` field: card images are already
 * external URLs (assets.st-note.com). Firebase Storage plus a FireCMS storage
 * adapter can replace this later without touching the site's read path.
 */
/**
 * **DEPRECATED 2026-09-03 — authoring only. The read path is untouched.**
 *
 * Both OneLive collections are retired from the CMS because neither has ever
 * held a record and neither is being authored. What that means precisely, since
 * "deprecated" could reasonably mean three different things here:
 *
 * - **Authoring is frozen.** `hideFromNavigation` takes it out of the drawer so
 *   an editor never meets it, and the permissions below refuse create, edit and
 *   delete. It stays *registered*, so a saved URL still resolves and the data
 *   remains inspectable — a soft delete, not a removal.
 * - **`apps/onelive` still reads it, and that is deliberate.** `SectionCaseGrid` on `/case` calls
 *   into this collection's data on every build and every mount. It renders
 *   nothing today only because the collection is empty, which is the same thing
 *   a reader sees now. Nothing about this change alters that.
 * - **The schema and the rules stay.** `packages/shared/cms/schema.ts` is
 *   imported by OneLive's live code, and `firestore.rules` still grants public
 *   read. Deleting either would break OneLive's build rather than tidy anything.
 *
 * **To revive it:** delete `hideFromNavigation` and `permissions` below. The
 * localised labels are still maintained in `src/i18n/collections/*`, so there is
 * nothing else to restore.
 *
 * Do not delete this file to "finish the job". Removing it while OneLive still
 * reads the collection would leave a read path with no way to author for it —
 * the failure would surface as an empty section nobody can fill.
 */
export const casesCollection = ({ cases: t }: CollectionStrings) =>
    buildCollection<CaseArticle>({
        id: "cases",
        path: "cases",
        name: t.collection.name,
        singularName: t.collection.singularName,
        icon: "Article",
        description: t.collection.description,
        /**
         * Deprecated — see the note above. Out of the navigation drawer, and
         * frozen against writes rather than deleted.
         */
        hideFromNavigation: true,
        permissions: { read: true, create: false, edit: false, delete: false },
        properties: {
            date: {
                dataType: "string",
                name: t.fields.date.name,
                description: t.fields.date.description,
                validation: { required: true, matches: /^\d{4}-\d{2}-\d{2}$/ },
            },
            title: {
                dataType: "map",
                name: t.fields.title.name,
                expanded: true,
                properties: {
                    zh: {
                        dataType: "string",
                        name: "繁體中文",
                        validation: { required: true },
                    },
                    jp: {
                        dataType: "string",
                        name: "日本語",
                        validation: { required: true },
                    },
                },
            },
            description: {
                dataType: "map",
                name: t.fields.description.name,
                expanded: true,
                properties: {
                    zh: {
                        dataType: "string",
                        name: "繁體中文",
                        multiline: true,
                        validation: { required: true },
                    },
                    jp: {
                        dataType: "string",
                        name: "日本語",
                        multiline: true,
                        validation: { required: true },
                    },
                },
            },
            img: {
                dataType: "string",
                name: t.fields.img.name,
                url: true,
                validation: { required: true },
            },
            url: {
                dataType: "string",
                name: t.fields.url.name,
                url: true,
                validation: { required: true },
            },
        },
    });
